import { useViewer } from "@/authentication/ViewerProvider"
import { Button } from "@/components/ui/button"
import Container from "@/components/ui/container"
import ErrorScreen from "@/components/ui/error-screen"
import { H1 } from "@/components/ui/headings"
import Panel from "@/components/ui/panel"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { ContributorPermission } from "@/generated/graphql"
import { cn } from "@/lib/utils"
import { MainLayout } from "@/novus/layouts/MainLayout"
import LoadingScreen from "@/novus/components/loading-screen"
import { rootPath } from "@/novus/novus-paths"
import { useMutation, useQuery } from "@apollo/client"
import React from "react"
import { SubmitHandler } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import FiveTwoNineAccountForm, {
  FiveTwoNineAccountFormValues,
} from "./components/FiveTwoNineAccountForm"

const CreateFiveTwoNineAccount_Mutation = gql(`
  mutation CreateFiveTwoNineAccount($input: CreateFiveTwoNineAccountInput!) {
    createFiveTwoNineAccount(input: $input) {
      fiveTwoNineAccount {
        ...ProfileFiveTwoNineAccountFragment
      }
      errors {
        path
        fullMessages
      }
    }
  }
`)

const CreateFiveTwoNineAccountScreen_Query = gql(`
  query CreateFiveTwoNineAccountScreen($userId: ID!) {
    fiveTwoNineAccounts(userId: $userId) {
      totalCount
    }
  }
`)

const AddFiveTwoNineAccountScreen = () => {
  const { toast } = useToast()
  const [mutate] = useMutation(CreateFiveTwoNineAccount_Mutation)
  const navigate = useNavigate()
  const { viewer } = useViewer()
  const { data, loading, error } = useQuery(
    CreateFiveTwoNineAccountScreen_Query,
    {
      variables: {
        userId: viewer.id,
      },
    },
  )

  if (loading && !data) return <LoadingScreen />
  if (error || !data) return <ErrorScreen message={error?.message} />

  const defaultValues: FiveTwoNineAccountFormValues = {
    sponsorStateId: "",
    programId: "",
    accountNumber: "",
    beneficiaryFirstName: "",
    beneficiaryLastName: "",
    beneficiaryBirthdate: "",
  }

  const onSubmit: SubmitHandler<FiveTwoNineAccountFormValues> = async (
    values,
  ) => {
    const { data } = await mutate({
      variables: {
        input: {
          programId: values.programId,
          beneficiaryFirstName: values.beneficiaryFirstName,
          beneficiaryLastName: values.beneficiaryLastName,
          beneficiaryBirthdate: values.beneficiaryBirthdate || undefined,
          accountNumber: values.accountNumber,
          accountOwnerFirstName: viewer.firstName || "",
          accountOwnerLastName: viewer.lastName || "",
          contributorPermission: ContributorPermission.Friends,
        },
      },
    })

    if (
      data?.createFiveTwoNineAccount.errors &&
      data?.createFiveTwoNineAccount.errors.length > 0
    ) {
      throw data?.createFiveTwoNineAccount.errors
    }

    toast({
      title: "Beneficiary has been added.",
    })

    navigate(rootPath.pattern)
  }

  return (
    <MainLayout>
      <Container>
        <Panel backPath={rootPath.pattern} minHeight={true}>
          <div className="text-center mb-14">
            <H1>Add Beneficiary</H1>
          </div>
          <FiveTwoNineAccountForm
            onSubmit={onSubmit}
            defaultValues={defaultValues}
            isNew={true}
          />
        </Panel>
        <div className="mt-6">
          <Panel>
            <div
              className={cn(
                "flex flex-col justify-between gap-2",
                "md:flex-row",
              )}
            >
              <div>
                <div className="font-semibold text-textPrimary text-2xl">
                  Don't have a 529 Account?
                </div>
                <div className="text-lg text-neutral-400">
                  Let us help you find the best 529 plan for you!
                </div>
              </div>
              <div className="flex items-center justify-center">
                <Button asChild={true} size="md" className="min-w-0">
                  <a
                    href="https://www.gohadley.com/find-a-plan"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    Find My 529
                  </a>
                </Button>
              </div>
            </div>
          </Panel>
        </div>
      </Container>
    </MainLayout>
  )
}

export default AddFiveTwoNineAccountScreen
